import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'

import { t } from '@ctcells/i18n'

import { Button } from '../../atoms/Button.tsx'
import { cx } from '../../atoms/cx.ts'

import { newExtra, type ExtraEntry } from './sampleRows.ts'

import '../../atoms/form.css'

/**
 * 시료 추가 입력 폼의 추가 입력(metadata) 편집(`ExtrasEditor`). 암종 · 병기 칩 입력(`TagInput`)은 걷었다 — 둘 다 하나만 고르는
 * `Combobox` 다(eric 2026-10-12 「시료가 하나잖아. 암종.. 하나만 선택 가능하게」).
 * 🔵 이 화면 전용이다 — 공용 원자로 올리지 않았다(쓰는 곳이 하나다).
 */

export interface ExtrasEditorProps {
  /** 편집기 제목 · 접근 이름(「2번 줄 추가 입력」) */
  title: string
  entries: readonly ExtraEntry[]
  onChange: (next: ExtraEntry[]) => void
  /** 문제가 있는 항목 */
  invalidIds: ReadonlySet<string>
  /** 그 문제의 사유 글자 — 편집기 바닥에 선다 */
  error?: string | undefined
  readOnly?: boolean | undefined
}

/**
 * 추가 입력(metadata) 편집 — 한 줄에 칸 둘(왼쪽 이름 · 오른쪽 값). 처음엔 빈 줄 하나가 늘 보이고, 아래 「+」로 한 줄씩 늘린다
 * (eric 2026-10-12 「예시는 빼고... 한 열에 두개의 입력창.. 왼쪽에는 키 오른쪽에는 값.. 하나만 보이고 그 밑에 + 버튼」).
 * 🔴 빈 줄 하나는 «보이기만» 한다 — 적기 전에는 항목(`entries`)이 아니라 metadata 로 나가지 않는다. 적는 순간 항목이 된다.
 * 키보드: 이름에서 Enter → 값 · 값에서 Enter → 다음 항목. **마지막 값의 Enter 는 폼이 받는다**(저장).
 */
export function ExtrasEditor({
  title,
  entries,
  onChange,
  invalidIds,
  error,
  readOnly,
}: ExtrasEditorProps) {
  const base = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  /** 다음 그리기 뒤에 짚을 칸 — 항목을 늘린 직후에는 그 칸이 아직 DOM 에 없다 */
  const focusNext = useRef<{ id: string; part: 'key' | 'value' } | null>(null)
  const locked = readOnly === true
  const errorId = `${base}-err`
  /** 항목이 없을 때 보이는 빈 줄 — 적으면 그 id 로 항목이 된다(칸 포커스가 그대로 이어진다) */
  const [blankRow, setBlankRow] = useState(() => newExtra(''))
  const rows = entries.length === 0 ? [blankRow] : entries

  useEffect(() => {
    const target = focusNext.current
    if (target === null) return
    focusNext.current = null
    rootRef.current
      ?.querySelector<HTMLInputElement>(`[data-extra="${target.id}"][data-part="${target.part}"]`)
      ?.focus()
  })

  const patch = (id: string, part: 'key' | 'value', v: string) =>
    onChange(
      rows.map((e) => (e.id !== id ? e : part === 'key' ? { ...e, key: v } : { ...e, value: v })),
    )

  const add = () => {
    // 맨 아래 줄이 아직 비어 있으면 그 줄을 짚기만 한다 — 빈 줄이 쌓이지 않게
    const last = rows[rows.length - 1]
    if (last !== undefined && last.key === '' && last.value === '') {
      focusNext.current = { id: last.id, part: 'key' }
      onChange([...entries])
      return
    }
    const fresh = newExtra('')
    focusNext.current = { id: fresh.id, part: 'key' }
    onChange([...rows, fresh])
  }

  const remove = (id: string) => {
    const at = entries.findIndex((e) => e.id === id)
    const next = entries.filter((e) => e.id !== id)
    const neighbor = next[Math.min(at, next.length - 1)]
    if (neighbor !== undefined) focusNext.current = { id: neighbor.id, part: 'key' }
    else {
      // 마지막 줄을 지웠다 — 새 빈 줄이 보인다
      const fresh = newExtra('')
      setBlankRow(fresh)
      focusNext.current = { id: fresh.id, part: 'key' }
    }
    onChange(next)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.nativeEvent.isComposing || e.key === 'Process') return
    if (e.key !== 'Enter' || !(e.target instanceof HTMLInputElement)) return
    const id = e.target.dataset.extra
    const part = e.target.dataset.part
    if (id === undefined || part === undefined) return
    if (part === 'key') {
      e.preventDefault()
      focusNext.current = { id, part: 'value' }
      onChange([...entries])
      return
    }
    const at = rows.findIndex((x) => x.id === id)
    const after = rows[at + 1]
    if (after === undefined) return // 마지막 값 — 폼이 받는다(저장)
    e.preventDefault()
    focusNext.current = { id: after.id, part: 'key' }
    onChange([...entries])
  }

  return (
    <div aria-label={title} className="snextras" onKeyDown={onKeyDown} ref={rootRef} role="group">
      <ul className="snextras__list">
        {rows.map((entry, i) => {
          const bad = invalidIds.has(entry.id)
          const n = i + 1
          return (
            <li className="snextras__item" key={entry.id}>
              <input
                aria-describedby={bad && error !== undefined ? errorId : undefined}
                aria-invalid={bad ? true : undefined}
                aria-label={t('sampleNew.extras.keyLabel', { n })}
                autoComplete="off"
                className={cx('a-input', 'a-input--wide', locked && 'a-input--readonly')}
                data-extra={entry.id}
                data-part="key"
                name="extra_key"
                onChange={locked ? undefined : (e) => patch(entry.id, 'key', e.target.value)}
                placeholder={t('sampleNew.extras.keyPlaceholder')}
                readOnly={locked}
                type="text"
                value={entry.key}
              />
              <input
                aria-describedby={bad && error !== undefined ? errorId : undefined}
                aria-invalid={bad ? true : undefined}
                aria-label={t('sampleNew.extras.valueLabel', {
                  name: entry.key.trim() === '' ? String(n) : entry.key.trim(),
                })}
                autoComplete="off"
                className={cx('a-input', 'a-input--wide', locked && 'a-input--readonly')}
                data-extra={entry.id}
                data-part="value"
                name="extra_value"
                onChange={locked ? undefined : (e) => patch(entry.id, 'value', e.target.value)}
                placeholder={t('sampleNew.extras.valuePlaceholder')}
                readOnly={locked}
                type="text"
                value={entry.value}
              />
              {/* 빈 줄 하나뿐이면 지울 것이 없다 — 자리만 비워 둔다(줄 폭이 안 흔들리게) */}
              {locked || entries.length === 0 ? (
                <span aria-hidden="true" className="snextras__noremove" />
              ) : (
                <Button
                  aria-label={t('sampleNew.extras.remove', {
                    name: entry.key.trim() === '' ? String(n) : entry.key.trim(),
                  })}
                  onClick={() => remove(entry.id)}
                  size="sm"
                >
                  <span aria-hidden="true">×</span>
                </Button>
              )}
            </li>
          )
        })}
      </ul>
      {error === undefined ? null : (
        <span className="a-field__error" id={errorId}>
          {error}
        </span>
      )}
      {/*
       * 한 줄 더 — 이름 · 값 줄과 같은 폭의 점선 줄(eric 2026-10-20 「추가 입력 너비를 다 차지하면서.. 자연스럽게.. 뭐를 추가하는지 구분이 안가」).
       * 점선 = «아직 없는 줄»(방안 A 의 점선 한 뜻). 종전 「+ 열 추가」 작은 단추는 무엇이 늘어나는지 말하지 않았다
       */}
      {locked ? null : (
        <button className="snextras__add" onClick={add} type="button">
          {t('sampleNew.extras.add')}
        </button>
      )}
    </div>
  )
}
